import type { CreateAlbumBody } from "./hostApi";
import { FILM_KEYS, SETTINGS_ENUMS, type Settings } from "./theme";
import { GUEST_TIERS, TRIAL } from "../../../worker/pricing";
import { t, type Key } from "./i18n";
import { translate, type Lang } from "../locales";

export { PHOTOS_PER_GUEST, TRIAL } from "../../../worker/pricing";

export type StepId =
  | "event" | "title" | "camera" | "filter" | "format"
  | "card" | "visibility" | "reveal" | "interaction" | "date" | "guests";

const ALL: StepId[] = [
  "event", "title", "camera", "filter", "format",
  "card", "visibility", "reveal", "interaction", "date", "guests",
];

type E = typeof SETTINGS_ENUMS;

export type Draft = {
  event_type: E["event_type"][number];
  title: string;
  camera: E["camera"][number] | null;
  filter_preset: string | null;
  instant_format: E["instant_format"][number];
  host_message: string;
  gallery_mode: "immediate" | "after_event" | "delayed";
  gallery_delay_min: number;
  visibility: E["visibility"][number];
  likes: boolean;
  comments: boolean;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  duration_min: number;
  guests: number;
  photos_per_guest: number;
};

export const TIERS = Object.keys(GUEST_TIERS).map(Number).sort((a, b) => a - b);

export const defaultDraft = (): Draft => ({
  event_type: "other",
  title: titleSuggestions("other")[0],
  camera: null,
  filter_preset: null,
  instant_format: "square",
  host_message: "",
  gallery_mode: "immediate",
  gallery_delay_min: 60,
  visibility: "all",
  likes: true,
  comments: false,
  ...defaultStart(new Date()),
  duration_min: 240,
  guests: TIERS[0],
  photos_per_guest: 24,
});

export const DURATIONS: { min: number; key: Key }[] = [
  { key: "host.wizard.duration.h1", min: 60 },
  { key: "host.wizard.duration.h2", min: 120 },
  { key: "host.wizard.duration.h4", min: 240 },
  { key: "host.wizard.duration.h12", min: 720 },
  { key: "host.wizard.duration.d1", min: 1440 },
  { key: "host.wizard.duration.d2", min: 2880 },
  { key: "host.wizard.duration.d3", min: 4320 },
];

export const EVENT_KEYS: Record<Draft["event_type"], Key> = {
  wedding: "events.wedding",
  birthday: "events.birthday",
  baby_shower: "events.baby_shower",
  travel: "events.travel",
  corporate: "events.corporate",
  other: "events.other",
};
/** Foto default per jenis acara (web/public/photos, lihat CREDITS.md). */
export const EVENT_PHOTO: Record<Draft["event_type"], string> = {
  wedding: "/photos/wedding.webp",
  birthday: "/photos/birthday.webp",
  baby_shower: "/photos/baby_shower.webp",
  travel: "/photos/travel.webp",
  corporate: "/photos/corporate.webp",
  other: "/photos/other.webp",
};
export const FORMAT_LABELS: Record<Draft["instant_format"], string> = { mini: "Mini", square: "Square", wide: "Wide" };
/** Saran judul per jenis acara; [0] jadi judul default saat jenis acara dipilih. */
export const titleSuggestions = (type: Draft["event_type"]): string[] =>
  [0, 1, 2, 3].map((i) => t(`host.wizard.titles.${type}_${i}` as Key));

const LANGS: Lang[] = ["en", "id"];
/** Judul == salah satu saran di bahasa manapun (bukan hanya bahasa aktif) → aman ditimpa saat ganti jenis acara. */
export const isSuggestedTitle = (type: Draft["event_type"], title: string): boolean =>
  LANGS.some((l) => [0, 1, 2, 3].some((i) => translate(l, `host.wizard.titles.${type}_${i}` as Key) === title));

/** Mulai default: jam bulat berikutnya (waktu lokal); dari 23:xx jadi besok 00:00. */
export function defaultStart(now: Date): { date: string; time: string } {
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1);
  return { date: isoDate(next), time: `${String(next.getHours()).padStart(2, "0")}:00` };
}

/** Query `?trial=` dari link: 1 / "1" / true / "true". */
export const parseTrial = (v: unknown): boolean => v === 1 || v === "1" || v === true || v === "true";

/** Hari ini di zona waktu lokal — batas bawah tanggal acara (toISOString memakai UTC, bisa geser sehari). */
const isoDate = (x: Date): string =>
  `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
export const todayISO = (): string => isoDate(new Date());

/** Tanpa visibilitas penuh tidak ada yang bisa diungkap, disukai, atau dikomentari. */
export const effective = (d: Draft): Draft =>
  d.visibility === "all" ? d : { ...d, gallery_mode: "immediate", likes: false, comments: false };

export const MODE_KEYS: Record<Draft["gallery_mode"], Key> = {
  immediate: "host.wizard.mode.immediate",
  after_event: "host.wizard.mode.after_event",
  delayed: "host.wizard.mode.delayed",
};

/** Langkah filter hanya untuk analogue; format hanya untuk instant; uji coba tanpa pilih paket. */
export function visibleSteps(d: Draft, trial = false): StepId[] {
  return ALL.filter((s) => {
    if (s === "filter") return d.camera === "analogue";
    if (s === "format") return d.camera === "instant";
    if (s === "reveal" || s === "interaction") return d.visibility === "all";
    if (s === "guests") return !trial;
    return true;
  });
}

export function stepValid(id: StepId, d: Draft): boolean {
  switch (id) {
    case "title": {
      const n = d.title.trim().length;
      return n >= 1 && n <= 80;
    }
    case "camera":
      return d.camera !== null;
    case "filter":
      return (FILM_KEYS as readonly string[]).includes(d.filter_preset ?? "");
    case "reveal":
      return (
        d.visibility !== "all" ||
        d.gallery_mode !== "delayed" ||
        (Number.isInteger(d.gallery_delay_min) && d.gallery_delay_min >= 1)
      );
    case "date":
      return d.date !== "" && d.time !== "" && d.date >= todayISO();
    default:
      return true;
  }
}

export function toBody(draft: Draft, trial = false): CreateAlbumBody {
  const d = effective(draft);
  if (!d.camera) throw new Error(t("host.wizard.camera_required"));
  const start = Math.floor(new Date(`${d.date}T${d.time}`).getTime() / 1000);
  const instant = d.camera === "instant";
  const settings: Settings = {
    event_type: d.event_type,
    camera: d.camera,
    visibility: d.visibility,
    likes: d.likes,
    comments: d.comments,
  };
  if (instant) settings.instant_format = d.instant_format;
  return {
    title: d.title.trim(),
    event_start: start,
    event_end: start + d.duration_min * 60,
    gallery_mode: d.gallery_mode,
    gallery_delay_min: d.gallery_mode === "delayed" ? d.gallery_delay_min : null,
    settings,
    filter_preset: instant ? "none" : (d.filter_preset ?? "none"),
    guests: trial ? TRIAL.guests : d.guests,
    photos_per_guest: trial ? TRIAL.photos_per_guest : d.photos_per_guest,
    host_message: d.host_message.trim() || null,
    trial: trial || undefined,
  };
}
